import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Gem, Trophy, ArrowLeft } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GrailGallery() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/grails`).then(({ data: d }) => setData(d)).catch(() => setData({ pulled: [], wild: [] }));
  }, []);

  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="grail-gallery-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/" data-testid="grails-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Pickle Bullpen
        </Link>
        <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-4">The Card Vault · Hall of Grails</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-3">
          Grail Gallery
        </h1>
        <p className="text-zinc-400 max-w-2xl mb-12">
          Every one-of-one card ever pulled from a Bullpen pack — only ONE copy of each exists on the planet,
          and this wall shows exactly who owns it.
        </p>

        {data.pulled.length === 0 && data.wild.length === 0 && (
          <div className="border border-dashed border-zinc-800 p-14 text-center text-zinc-600 uppercase tracking-widest text-sm" data-testid="grails-empty">
            No one-of-ones exist yet — when the first grail drops, it lands here.
          </div>
        )}

        {data.pulled.length > 0 && (
          <div className="mb-14" data-testid="grails-pulled">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl mb-6">
              <Trophy className="w-5 h-5" /> Claimed Grails
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.pulled.map((g, i) => (
                <div key={i} data-testid={`grail-card-${i}`}
                  className="group border border-amber-400/30 bg-white/[0.03] p-4 transition-transform duration-200 hover:-translate-y-1">
                  <div className="relative">
                    <img src={g.url} alt={g.name} className="w-full aspect-[3/4] object-contain bg-black border border-amber-400/40 cursor-zoom-in"
                      title="Tap to zoom" onClick={() => zoomCard(g.url, g.name)} />
                    <span className="absolute top-2 left-2 flex items-center gap-1 bg-black/80 border border-amber-400/60 text-amber-400 text-[9px] font-black uppercase tracking-widest px-2 py-1">
                      <Gem className="w-3 h-3" /> 1 of 1
                    </span>
                  </div>
                  <div className="mt-3 font-display font-bold uppercase truncate">{g.name}</div>
                  <div className="flex items-center gap-2 mt-2">
                    {g.owner_avatar ? (
                      <img src={g.owner_avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-zinc-700" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-lime-400/20 border border-lime-400/40 flex items-center justify-center text-[9px] font-black text-lime-300">
                        {(g.owner_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </span>
                    )}
                    <div className="text-xs">
                      <span className="text-zinc-500">Owned by </span>
                      <span className="text-white font-semibold" data-testid={`grail-owner-${i}`}>{g.owner_name}</span>
                    </div>
                  </div>
                  {g.pulled_at && (
                    <div className="text-[10px] text-zinc-600 mt-1.5">
                      Pulled {new Date(g.pulled_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {data.wild.length > 0 && (
          <div className="mb-14" data-testid="grails-wild">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase tracking-wide text-red-400 text-2xl mb-2">
              <Gem className="w-5 h-5" /> Still in the Wild
            </h2>
            <p className="text-xs text-zinc-500 mb-6">These one-of-ones are hiding in packs RIGHT NOW — whoever pulls one owns the only copy forever.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {data.wild.map((g, i) => (
                <div key={i} data-testid={`grail-wild-${i}`} className="border border-red-400/30 bg-white/[0.03] p-3">
                  <img src={g.url} alt={g.name} className="w-full aspect-[3/4] object-contain bg-black border border-red-400/30 saturate-50 contrast-125 cursor-zoom-in"
                    title="Tap to zoom" onClick={() => zoomCard(g.url, g.name)} />
                  <div className="mt-2 text-[11px] font-bold uppercase truncate">{g.name}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-red-400 animate-pulse">Unclaimed</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center border-t border-zinc-900 pt-10">
          <p className="text-zinc-400 mb-4">Want your name on this wall? Rip packs in the Card Vault.</p>
          <Link to="/auth?mode=register" data-testid="grails-join-btn"
            className="inline-flex items-center gap-2 rounded-full bg-lime-400 text-black font-display font-bold uppercase tracking-wider px-8 py-3 hover:bg-lime-300 transition-colors">
            Enter the Bullpen
          </Link>
        </div>
      </div>
    </div>
  );
}
